import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import eventTypes from 'theme/eventTypes';
import { useEvent } from 'context/events/Event';
import { useBackButtonCollapse } from 'context/general/BackButtonCollapse';
// import useAuth from 'hooks/useAuth';
import { Calendar, Clock, DollarSign, Hash, Heart, MapPin, Share, Share2, Tag, X } from 'lucide-react';
import Modal from 'components/_UI/Modal';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCollapse } from 'context/general/Collapse';
import { useInvisibleButton } from 'context/general/InvisibleButton';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import DescriptionWithHTML from './DescriptionWithHTML';
import Text from 'components/_UI/Text';
import LinkPage from 'components/_UI/LinkPage';
import types from 'helpers/system/types';
import fonts from 'theme/fonts';
import Icon from 'components/_UI/Icon';
import InfoTooltip from 'components/_UI/InfoTooltip';
import Button from 'components/_UI/Button';
import Loader from 'components/_UI/Loader';
import eventApi from 'api/requests/events';
import date from 'helpers/date';
import { useAuth } from 'context/auth/Auth';
import useFavorite from 'hooks/useFavorite';
import useShare from 'hooks/useShare';
import currency from 'helpers/currency';

const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/**
 * Endereço legível a partir do formato estruturado, caindo para `raw_address`.
 *
 * O card da listagem já fazia esse fallback; a modal não, então eventos cujo
 * endereço vem só como texto livre (os mapeados por IA, por exemplo) exibiam
 * o ícone de local com a linha vazia ao lado.
 */
function formatAddress(address) {
    if (!address) {
        return null;
    }

    const parts = [
        address?.street?.name,
        address?.number && `nº ${address.number}`,
        address?.street?.district && `Bairro ${address.street.district}`,
        address?.complement,
    ].filter(Boolean);

    return parts.length ? parts.join(', ') : (address?.raw_address || null);
}

export default function EventDetailV2({ data, fromHome = false, onClose }) {
    const textIa = 'kaue.ia'

    // const navigate = useNavigate();
    // const location = useLocation();
    // const { collapse, setCollapse } = useCollapse();
    // const { backButtonCollapse, setBackButtonCollapse } = useBackButtonCollapse();

    // const [fullCronogramModal, setFullCronogramModal] = useState(false);
    // const [goToLoginModal, setGoToLoginModal] = useState(false);
    const [hasTickets, setHastickets] = useState(null);
    const [hasProducts, setHasProducts] = useState(null);
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [ticketUrl, setTicketUrl] = useState(false);
    const { setInvisibleButton } = useInvisibleButton();
    const { isFavorited, toggleFavorite } = useFavorite(data?.slug, data?.is_favorited);
    const { share } = useShare();
    const [linkCopied, setLinkCopied] = useState(false);
    const { isAuth } = useAuth();

    function handleFavorite() {
        if (!isAuth && onClose) {
            onClose();
        }
        toggleFavorite();
    }

    useEffect(() => {
        getEvent(data?.slug);
    }, []);

    async function getEvent(slug) {
        setLoading(true);
        try {
            const { data: eventData } = await eventApi.getBySlug(slug, fromHome ? { fromHome: true } : {});
            setContent(eventData?.data);
            setTicketUrl(`${import.meta.env.VITE_CLIENT_URL}/evento/${data?.slug}`);
            setHastickets(eventData?.data?.products?.filter(el => el.type == 'ticket'));
            setHasProducts(eventData?.data?.products?.filter(el => el.type == 'product'));
        } catch (error) {
            console.error('Error fetching event:', error);
        } finally {
            setLoading(false);
        }
    }

    const addressText = formatAddress(content?.address);

    const firstDate = content?.dates?.[0];
    const weekday = firstDate?.date ? WEEKDAYS[new Date(`${firstDate.date}T12:00:00`).getDay()] : null;
    const extraDatesCount = Math.max(0, (content?.dates?.length || 0) - 1);

    // Menor preço entre os ingressos: é o critério de decisão que mais faltava aqui.
    // "Pago" sozinho não diz se são R$ 20 ou R$ 200.
    const ticketPrices = (hasTickets || [])
        .map(item => Number(item?.price))
        .filter(price => Number.isFinite(price) && price > 0);

    const lowestPrice = ticketPrices.length ? Math.min(...ticketPrices) : null;
    const isFree = content?.classification?.name === eventTypes.free.slug;

    // O mesmo rodapé servia dois blocos quase idênticos, e ambos podiam renderizar
    // juntos quando o evento tinha ingressos internos e link externo — dois CTAs
    // "Garanta seu ingresso" empilhados. Agora a origem do link é decidida aqui.
    const hasInternalTickets = !!hasTickets?.length || !!hasProducts?.length;
    const externalTicketLink = content?.link_ticket;
    const showFooter = hasInternalTickets || !!externalTicketLink;
    const ticketHref = hasInternalTickets ? ticketUrl : externalTicketLink;
    const isExternalTicket = !hasInternalTickets && !!externalTicketLink;

    async function handleShare() {
        const shareUrl = content?.link_ticket || ticketUrl || `${window.location.origin}/evento/${data?.slug}`;
        const result = await share({
            title: content?.name,
            url: shareUrl,
        });

        if (result?.method === 'clipboard' && result?.success) {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2500);
        }
    }

    return (
        <Container>
            {
                loading ? (
                    <FullyCentered height='250px'>
                        <Loader />
                    </FullyCentered>
                ) : (
                    content?.name && (
                        <>
                            <StyledBackgroundContainer>
                                {onClose && (
                                    <MobileCloseBtn onClick={onClose}>
                                        <X size={18} />
                                    </MobileCloseBtn>
                                )}
                                {/* Cartazes de evento costumam ser verticais: com `cover`
                                    eles eram cortados no meio e perdiam justamente as
                                    informações impressas. A arte agora aparece inteira
                                    sobre uma cópia borrada dela mesma, que preenche a
                                    faixa sem barras vazias. */}
                                <BackgroundBlur
                                    aria-hidden='true'
                                    src={content?.images?.[0] || '/assets/images/placeholder-events.png'}
                                />
                                <StyledBackground
                                    src={content?.images?.[0] || '/assets/images/placeholder-events.png'}
                                    alt={`Cartaz do evento ${content.name}`}
                                    onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                                />

                                <OverlayContent>
                                    <EventTitle>{content.name}</EventTitle>

                                    <MetaRow>
                                        <MetaItem>
                                            <Calendar size={14} color={colors.neutralLight} />
                                            <span>{weekday ? `${weekday}, ` : ''}{date.format(firstDate?.date)}</span>
                                        </MetaItem>

                                        {firstDate?.start && (
                                            <MetaItem>
                                                <Clock size={14} color={colors.neutralLight} />
                                                <span>{date.formatTime(firstDate.start)}</span>
                                            </MetaItem>
                                        )}
                                    </MetaRow>

                                    {extraDatesCount > 0 && (
                                        <Text name='small' color={colors.neutralLight} paddingBottom={0} marginTop='4px'>
                                            +{extraDatesCount} {extraDatesCount === 1 ? 'outra data' : 'outras datas'}
                                        </Text>
                                    )}
                                </OverlayContent>
                            </StyledBackgroundContainer>

                            <ContentContainer>
                                <TagsRow marginTop='-6px'>
                                    {/* <FullyCentered width='auto'>
                        <DollarSign
                            color={content?.classification?.name == eventTypes.free.slug ? eventTypes.free.color : eventTypes.paid.color}
                        />
                        <Text
                            paddingBottom={0}
                            type='bold'
                            color={content?.classification?.name == eventTypes.free.slug ? eventTypes.free.color : eventTypes.paid.color}
                        >
                            Evento {types.getBySlug(content?.classification?.name).label}
                        </Text>
                    </FullyCentered> */}
                                    {/* types.getBySlug() não resolvia: eventTypes.paid.slug é
                                        'private', mas a API devolve 'paid' em classification.name.
                                        O label vinha undefined e o chip exibia só "Evento". */}
                                    <TagItem type={isFree ? eventTypes.free.slug : eventTypes.paid.slug}>
                                        <Text
                                            color={colors.neutralLight}
                                            type='bold'
                                            name='small'
                                        >
                                            {isFree ? 'Gratuito' : 'Pago'}
                                        </Text>
                                    </TagItem>

                                    {
                                        content?.adulthood && (
                                            <TagItem adulthood>
                                                <Text
                                                    color={colors.neutralLight}
                                                    type='bold'
                                                    name='small'
                                                > +18
                                                </Text>
                                            </TagItem>
                                        )
                                    }

                                    {content?.tags?.map(el => (

                                        <TagItem key={el.id ?? el.name}>
                                            <Text
                                                color={colors.neutralLight}
                                                type='bold'
                                                name='small'
                                            >
                                                {el.name}
                                            </Text>
                                        </TagItem>

                                    ))}
                                </TagsRow>

                                {(isFree || lowestPrice) && (
                                    <PriceRow>
                                        <PriceLabel>{isFree ? 'Entrada' : 'Ingressos a partir de'}</PriceLabel>
                                        <PriceValue isFree={isFree}>
                                            {isFree ? 'Gratuita' : currency.format(lowestPrice)}
                                        </PriceValue>
                                    </PriceRow>
                                )}

                                {/* <CategoriesInfos>
                    <FullyCentered width='auto'>
                        <Tag color={colors.occasionalPurple} />
                    </FullyCentered>


                    {content?.tags?.map(el => (
                        <Text marginLeft={'6px'} >
                            #{el.name}
                        </Text>
                    ))}
                </CategoriesInfos> */}

                                {
                                    !content?.hide_address && (
                                        <ContentInfoRow address>
                                            <InfoIcon>
                                                <MapPin color={colors.occasionalPurple} size={18} />
                                            </InfoIcon>
                                            {
                                                addressText ? (
                                                    <ContentLinkAddress>
                                                        <LinkPage isWrapper to={content.link_map}>
                                                            <Text color={colors.primaryDark} decoration='underline' paddingBottom={'0px'}>
                                                                {addressText}
                                                            </Text>
                                                        </LinkPage>
                                                    </ContentLinkAddress>
                                                ) : (
                                                    /* Cinza, não roxo: "Local a definir" é ausência de
                                                       informação e não deve receber a cor reservada a
                                                       links e destaques. */
                                                    <Text color={colors.night} paddingBottom={'0px'}>
                                                        Local a definir
                                                    </Text>
                                                )
                                            }

                                        </ContentInfoRow>
                                    )
                                }

                                {
                                    content?.origin === textIa && (
                                        <Row verticalCenter marginBottom='15px'>
                                            <StyledIa>
                                                <Icon name='smartToy' color={colors.occasionalPurple} />
                                            </StyledIa>

                                            <Text paddingBottom={'0'} color={colors.nightDark}>Evento mapeado através de IA</Text>

                                            <InfoTooltip
                                                bgColor={colors.night}
                                                text=<>O Whizr usa inteligência artificial para o mapeamento de eventos que não são cadastrados através de parceiros oficiais.
                                                    <br></br>
                                                    Recomendamos checar as informações no local oficial do evento.</>
                                                type='info'
                                            />
                                        </Row>
                                    )}

                                {
                                    content?.establishment?.name && (
                                        <Row width='100%'>
                                            <LinkPage target={'_blank'} to={`/estabelecimento/${content?.establishment?.slug}/detalhes`}>
                                                <ContentOrganizer>
                                                    <DynamicProfileImage
                                                        size='24px'
                                                        img={content?.establishment?.profile_image_id?.thumb}
                                                        word={content?.establishment?.name}
                                                        textSize='normal'
                                                    />
                                                    <Text color={colors.nightDark} paddingBottom={'0'} marginLeft={'8px'} marginRight={'5px'}>Organizado por</Text>
                                                    <Text color={colors.nightDark} type='bold' paddingBottom={'0px'}>{content?.establishment?.name}</Text>
                                                </ContentOrganizer>
                                            </LinkPage>
                                        </Row>
                                    )
                                }

                                <SpecialDivider />

                            </ContentContainer>

                            <Description>
                                {/* Era 'Sobre o evento:' em roxo grande, competindo com o
                                    título e com o CTA. Como rótulo de seção, precisa
                                    apenas marcar o início do bloco. */}
                                <SectionLabel>Sobre o evento</SectionLabel>
                                {
                                    content?.description_with_html
                                        ? (
                                            <DescriptionWithHtmlContainer>
                                                <DescriptionWithHTML data={content?.description_with_html} />
                                            </DescriptionWithHtmlContainer>
                                        ) : (
                                            <Text
                                                color={colors.nightDark}
                                                marginTop='2px'
                                                marginBottom='16px'
                                            >
                                                {content.description}
                                            </Text>
                                        )
                                }
                            </Description>

                            {showFooter && (
                                <Footer>
                                    <Row>
                                        <SpecialButton
                                            category='squiredBorded'
                                            variant='featured'
                                            href={ticketHref}
                                            /* Link de parceiro abre em nova aba; a página
                                               do próprio evento navega na mesma, para não
                                               espalhar abas do site dentro do site. */
                                            target={isExternalTicket ? '_blank' : undefined}
                                            rel={isExternalTicket ? 'noopener noreferrer' : undefined}
                                        >
                                            <Column width='18px' height='18px'>
                                                <Icon name='diagonal-ticket' color={colors.neutralLight} />
                                            </Column>
                                            <Text name='highlight' color={colors.neutralLight} paddingBottom={0} marginLeft={'6px'}>
                                                Garanta seu ingresso
                                            </Text>
                                        </SpecialButton>

                                        <SpecialButton
                                            onClick={handleFavorite}
                                            aria-pressed={isFavorited}
                                            aria-label={isFavorited ? 'Remover dos favoritos' : 'Favoritar evento'}
                                        >
                                            <Heart
                                                color={colors.occasionalPurple}
                                                fill={isFavorited ? colors.occasionalPurple : 'none'}
                                                size={18}
                                            />
                                        </SpecialButton>

                                        <SpecialButton onClick={handleShare} aria-label='Compartilhar evento'>
                                            {linkCopied
                                                ? <Text name='small' paddingBottom={0} color={colors.occasionalPurple}>Copiado!</Text>
                                                : <Share2 color={colors.occasionalPurple} size={18} />
                                            }
                                        </SpecialButton>
                                    </Row>
                                </Footer>
                            )}
                        </>
                    )
                )
            }
        </Container>
    );
}

const MobileCloseBtn = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(4px);
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #fff;
    flex-shrink: 0;
`;

const Container = styled(Column)`
    position: relative;
`;

const StyledBackgroundContainer = styled(FullyCentered)`
    width: 100%;
    height: auto;
    position: relative;
    background: ${colors.neutralDark};
    overflow: hidden;
`;

const StyledBackground = styled.img`
    position: relative;
    z-index: 1;
    width: auto;
    max-width: 100%;
    height: 200px;
    object-fit: contain;

    @media screen and (min-width: ${breakpoints.md}) {
        height: 290px;
    }
`;

const BackgroundBlur = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: blur(18px) brightness(0.75);
    transform: scale(1.15);
    z-index: 0;
`;

const EventTitle = styled.h2`
    margin: 0;
    font-size: 19px;
    line-height: 1.3;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${colors.neutralLight};
    text-shadow: 0 1px 12px rgba(0, 0, 0, 0.4);

    /* Títulos longos viravam um paredão de 3-4 linhas sobre a arte.
       Duas linhas bastam para identificar; o nome completo segue no
       texto do evento logo abaixo. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @media screen and (min-width: ${breakpoints.md}) {
        font-size: 22px;
    }
`;

const MetaRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 14px;
    margin-top: 6px;
`;

const MetaItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    /* Data e hora não devem partir no meio: "sábado," numa linha e a data
       na outra custa mais leitura do que a economia de espaço vale. */
    white-space: nowrap;
    font-size: 14px;
    color: ${colors.neutralLight};
`;

const PriceRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    margin-top: 16px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #FAF7FF;
    border: 1px solid rgba(105, 12, 228, 0.12);
`;

const PriceLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: ${colors.night};
`;

const PriceValue = styled.strong`
    font-size: 23px;
    line-height: 1.2;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${({ isFree }) => isFree ? colors.successDark : colors.nightDark};
`;

const OverlayContent = styled(Column)`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    /* Acima da arte e do fundo borrado: com o cartaz em contain ele passa
       na frente do título se a faixa não tiver empilhamento explícito. */
    z-index: 2;
    padding: 40px 16px 14px;

    /* Gradiente no lugar da tarja preta de 70%: a faixa opaca cortava a arte
       em dois retângulos e pesava a composição. O degradê ancora o texto
       escurecendo só o necessário e deixa a imagem respirar. */
    background: linear-gradient(
        to top,
        rgba(12, 8, 18, 0.94) 0%,
        rgba(12, 8, 18, 0.78) 45%,
        rgba(12, 8, 18, 0) 100%
    );

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 52px 22px 18px;
    }
`;

const ContentContainer = styled(Column)`
    padding: 14px 14px 0 14px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 20px 20px 0 20px;
    }
`;


const ContentInfoRow = styled(Row)`
    margin-bottom: 10px;
    align-items: flex-start;
    text-align: left;
    margin-top: 14px;

    ${({ address }) => address && `
        align-items: center;
    `}
    
    ${({ single }) => single && `
        align-items: center;

        & > p {
            padding-bottom: 0 !important;
        }
    `}

    &:nth-child(3){
        margin-bottom: 0;
    }

    & > p {
        padding: 0;
        margin: 0;
    }

    & > a > p{
        padding-bottom: 0 !important;
    }
`;

const ContentLinkAddress = styled.div`
    width: auto;
`;

const CategoriesInfos = styled(Row)`
    width: 100%;
    align-items: center;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    margin-top: 10px;
    margin-bottom: 16px;

    & > p {
        padding-bottom: 0;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        margin-bottom: 10px;    
        justify-content: flex-start;
    }
    
`;

const EventType = styled.div`
    color: ${colors.neutralLight};
    border-radius: 30px;
    padding: 4px 10px;
    font-size: ${fonts.sizes.small};
    text-transform: capitalize;
    margin-top: 8px;

    ${({ type }) => type === eventTypes.free.slug && `
        background: ${eventTypes.free.color};
    `}

    ${({ type }) => type === eventTypes.paid.slug && `
        background: ${eventTypes.paid.color};
    `}
`;


const TagsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
`;

/**
 * Chips tonais em vez de pílulas chapadas.
 *
 * As cores saturadas em bloco (laranja, roxo, verde cheios, raio 30px) faziam
 * cada tag competir com o CTA e davam ar de etiqueta de promoção. Fundo suave
 * com texto na cor forte mantém a leitura da categoria sem gritar, e o raio
 * menor alinha os chips ao resto da interface.
 */
const TagItem = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 5px 10px;
    border-radius: 8px;
    background: ${colors.neutralDark};
    width: auto;

    & > p {
        padding-bottom: 0 !important;
        text-transform: capitalize !important;
        color: ${colors.night} !important;
        font-weight: 600 !important;
    }

    ${({ adulthood }) => adulthood && `
        background: ${colors.nightDark};

        & > p {
            color: ${colors.neutralLight} !important;
        }
    `};

    ${({ type }) => type === eventTypes.free.slug && `
        background: rgba(43, 200, 102, 0.14);

        & > p {
            color: ${colors.successDark} !important;
        }
    `};

    ${({ type }) => type === eventTypes.paid.slug && `
        background: #F3EAFF;

        & > p {
            color: ${colors.occasionalPurple} !important;
        }
    `};
`;

const SectionLabel = styled.h3`
    margin: 14px 0 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.night};
`;

const Description = styled(Column)`
    padding: 4px 14px 30px 14px;
    align-items: flex-start;
    text-align: left;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 4px 22px 30px 22px;
    }
`;

const DescriptionWithHtmlContainer = styled(Column)`
    /* @media screen and (min-width: ${breakpoints.sm}) {
        padding: 0 26px;
    } */
`;

/**
 * Coluna de ícones das linhas de informação.
 *
 * Pin (20px), robô da IA (22px) e avatar do organizador (24px) tinham larguras
 * diferentes, então cada texto ao lado começava num x distinto e a lista de
 * informações ficava serrilhada. Uma caixa de largura fixa alinha todas.
 */
const InfoIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    min-width: 24px;
    margin-right: 8px;
`;

const StyledIa = styled(InfoIcon)`
    & > * {
        width: 20px;
        height: 20px;
    }
`;

const ContentOrganizer = styled(Row)`
    width: 100%;
    align-items: center;
    justify-content: flex-start;
    /* margin-bottom: 15px; */
    text-align: left;

    transition: all ease-in-out .25s;
    &:hover {
        transform: scale(1.01)
    }
`;

const Footer = styled(Row)`
    position: sticky;
    bottom: 0;
    left: 0;
    /* Branco sólido: o fundo translúcido deixava o texto do evento passar
       por trás do CTA durante o scroll. A sombra ascendente já comunica
       que a barra flutua sobre o conteúdo. */
    background: ${colors.neutralLight};
    padding: 12px 14px;
    border-top: 1px solid rgba(26, 26, 26, 0.08);
    box-shadow: 0 -6px 20px rgba(26, 26, 26, 0.06);
    z-index: 10;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 14px 22px;
    }

    @media screen and (max-width: 899px) {
        padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 16px);
        border-radius: 0 0 16px 16px;
    }
`;

const SpecialButton = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    min-height: 48px;
    min-width: 48px;
    border-radius: 12px;
    margin-left: 10px;
    border: none;
    transition: background 0.18s ease, opacity 0.18s ease, transform 0.18s ease;
    cursor: pointer;

    &:first-child {
        flex: 1;
        margin-left: 0;
        background: ${colors.occasionalPurple};

        & > h3 {
            font-weight: ${fonts.weight.bold} !important;
            color: ${colors.neutralLight};
        }

        &:hover {
            opacity: 0.92;
        }
    }

    /* Ações secundárias como círculos neutros. Antes tinham borda roxa 1px
       e fundo branco translúcido, o que dava a elas o mesmo peso visual do
       CTA e triplicava a quantidade de roxo na barra. */
    &:not(:first-child) {
        border-radius: 50%;
        background: ${colors.neutralDark};

        &:hover {
            background: #F3EAFF;
        }
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

/* #ACACAC deixava um risco duro atravessando o card. Uma divisória deve
   separar sem ser lida como elemento. */
const SpecialDivider = styled.div`
    width: 100%;
    height: 1px;
    background: rgba(26, 26, 26, 0.08);
    margin: 18px 0 4px;
`;